import React, { useContext, useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import isNil from 'lodash/isNil';

// GLOBAL VARIABLES
import { RETAIL_WHOLESALE, WHOLESALE_FORMAT } from 'codes';

// FUNCTIONS
import { formatDateTime } from 'functions';

// GLOBAL HELPERS/CONTEXT
import { RecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import Card from 'parts/card/Card';

// PARTS
import Form from 'core/form/Form';

// LOCAL COMPONENTS
import AMFIProductsForm from 'routers/interface/forms/amfiProducts/AMFIProducts';
import HeaderData from 'routers/interface/tools/HeaderData';

// LOCAL VARIABLES
import {
  CUSTOMER_KEYS,
  FINANCESTAFF_KEYS,
  IS_FINANCED_OPTIONS,
  LENDER_KEYS,
  LOOKUP_PARAMS,
  STATES_KEYS
} from '../variables';

// STYLES
import styles from './afterMarketFinance.module.scss';

// FORM COMOPONENT
const AfterMarketFinanceForm = () => {
  // CONTEXT
  const { record } = useContext(RecordContext);
  const { values = {} } = useFormikContext() || {};

  // MEMOS
  const { dsl_lender_id, is_financed } = useMemo(() => values || {}, [values]);
  const { fm_assignment_time, fm_assignment_user_id } = useMemo(() => record || {}, [record]);

  const fmTimeAssigned = useMemo(() => formatDateTime(fm_assignment_time, 4, true), [fm_assignment_time]);

  const fmAssignmentLookupParams = useMemo(() => ({
    ...LOOKUP_PARAMS.finance,
    siteUserIdList: fm_assignment_user_id,
  }), [fm_assignment_user_id]);

  // RENDER
  return (
    <Form.Body>
      <Form.Select
        name="fm_assignment_user_id"
        label="FM Assignment"
        lookup="fmAssignment"
        optionKeys={FINANCESTAFF_KEYS}
        lookupParams={fmAssignmentLookupParams}
        useNone
      />
      <Form.PlainText value={fmTimeAssigned} label="FM Time Assigned" />
      <Card.Divider />
      <Form.Checklist
        type="radio"
        name="is_financed"
        label="Funded By"
        options={IS_FINANCED_OPTIONS}
        list={{
          direction: 'horizontal',
          justify: 'start'
        }}
      />
      {isNil(is_financed) ? null : String(is_financed) === 'false' ? (
        <Form.Select
          name="dsl_customer_source_id"
          label="Funding Source"
          lookup="customer"
          optionKeys={CUSTOMER_KEYS}
          useNone
        />
      ) : (
        <>
          <Form.Select
            name="dsl_lender_id"
            label="Funding Source"
            lookup="lender"
            optionKeys={LENDER_KEYS}
            required
            useNone
          />
          {dsl_lender_id === 0 && <Form.Control name="lender_text" label="Lender" />}
        </>
      )}
      <Card.Divider />
      <Form.Col>
        <AMFIProductsForm />
      </Form.Col>
      <Card.Divider />
      <Form.Control name="customer_number" label="Customer Number" required />
      <Form.Select name="tag_state_code" label="Tag State" lookup="states" optionKeys={STATES_KEYS} required useNone />
      <Form.Control name="deal_number" label="Deal Number" required />
    </Form.Body>
  );
};

export default function AfterMarketFinance(props) {
  // CONTEXT
  const {
    record: {
      inventory = {}, //
      sale_type_category_code,
      wholesale_format_id,
      process_through_finance
    } = {}
  } = useContext(RecordContext);

  // MEMOS
  const { make, model, stock_num, vehicle_year } = useMemo(() => inventory || {}, [inventory]);

  const isWholesale = useMemo(
    () => sale_type_category_code === RETAIL_WHOLESALE.wholesale.category_code,
    [sale_type_category_code]
  );
  const isWholesaleSimplified = useMemo(
    () => isWholesale && wholesale_format_id === WHOLESALE_FORMAT.simplified.numeric,
    [isWholesale, wholesale_format_id]
  );

  // RENDER
  return (
    <Card
      className={styles.card}
      title="Aftermarket / Finance"
      tools={<HeaderData stockNo={stock_num} year={vehicle_year} make={make} model={model} />}
      body={
        isWholesaleSimplified && process_through_finance === false ? (
          <div className="my-3 text-center large">Not processed through Finance</div>
        ) : (
          <AfterMarketFinanceForm {...props} />
        )
      }
    />
  );
}
